/**
 * Empty State
 * Shown when no campaign or creative is selected
 */

import React, { useState } from 'react';
import { Folder, Plus, ChevronRight } from 'lucide-react';
import { useCreativeStore } from '@/stores';
import { NewCreativeDialog } from '@/components/ui/NewCreativeDialog';

export function EmptyState() {
  const {
    campaigns,
    currentCampaignId,
    isLoading,
    createCampaign,
    selectCampaign,
  } = useCreativeStore();

  // All hooks must be at the top level, before any conditional returns
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showNewCreativeDialog, setShowNewCreativeDialog] = useState(false);

  const handleCreate = async () => {
    if (newName.trim()) {
      const campaign = await createCampaign(newName.trim());
      selectCampaign(campaign.id);
      setNewName('');
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-navy">
        <div className="text-fg-muted">Loading...</div>
      </div>
    );
  }

  // No campaigns exist - prompt to create first one
  if (campaigns.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-navy">
        <div className="max-w-md text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Folder size={32} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-fg mb-2">
            Create your first campaign
          </h2>
          <p className="text-fg-muted mb-6">
            Campaigns organize your ad creatives. Create one to get started.
          </p>

          {isCreating ? (
            <div className="space-y-3">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Campaign name"
                className="input w-full text-center"
                autoFocus
              />
              <div className="flex justify-center gap-3">
                <button onClick={handleCreate} className="btn-primary px-6">
                  Create
                </button>
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setNewName('');
                  }}
                  className="btn-secondary px-6"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="btn-primary px-6 py-3 flex items-center gap-2 mx-auto"
            >
              <Plus size={18} />
              New Campaign
            </button>
          )}
        </div>
      </div>
    );
  }

  // Campaigns exist but none selected - prompt to select
  if (!currentCampaignId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-navy">
        <div className="max-w-md text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
            <Folder size={32} className="text-fg-muted" />
          </div>
          <h2 className="text-xl font-semibold text-fg mb-2">
            Select a campaign
          </h2>
          <p className="text-fg-muted mb-6">
            Choose a campaign from the sidebar to view and edit your creatives.
          </p>

          <div className="bg-white/5 rounded-xl p-2 max-h-64 overflow-y-auto">
            {campaigns.map((campaign) => (
              <button
                key={campaign.id}
                onClick={() => selectCampaign(campaign.id)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <Folder size={18} className="text-fg-muted" />
                  <span className="text-fg">{campaign.name}</span>
                </div>
                <ChevronRight size={16} className="text-fg-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Campaign selected but no creative - prompt to create/select creative
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center bg-navy">
        <div className="max-w-md text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
            <Plus size={32} className="text-fg-muted" />
          </div>
          <h2 className="text-xl font-semibold text-fg mb-2">
            No creative selected
          </h2>
          <p className="text-fg-muted mb-6">
            Select an existing creative from the sidebar, or create a new one.
          </p>
          <button
            onClick={() => setShowNewCreativeDialog(true)}
            className="btn-primary px-6 py-3 flex items-center gap-2 mx-auto"
          >
            <Plus size={18} />
            New Creative
          </button>
        </div>
      </div>

      <NewCreativeDialog
        isOpen={showNewCreativeDialog}
        onClose={() => setShowNewCreativeDialog(false)}
      />
    </>
  );
}
